'use client'

import { useContext, useState } from 'react'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Navigation } from 'swiper/modules'
import { motion } from 'framer-motion'
import 'swiper/css'
import 'swiper/css/navigation'

import LazyImage from '@/components/common/LazyImage'
import { WebContext } from '@/context/webContext'

import s from './Gallery.module.scss'

interface GalleryProps {
    hide: boolean | null;
    imageUrl: string;
    metadata: {
        dimensions: {
            width: number;
            height: number;
        }
        lqip?: string;
    }
    ref: string;
    attribution: string;
    caption: any;
    filename: string;
}

interface DataProps {
    gallery: Array<GalleryProps> | null;
    galleryHidden: Array<GalleryProps> | null;
    title: string;
    initialIndex: number;
    onClose: () => void;
}

export default function GalleryProjectComponent({ gallery, galleryHidden, title, initialIndex, onClose }: DataProps) {
    const { openOverview } = useContext(WebContext)

    // galleryHidden trae la galería completa; si viene vacía usamos la visible
    // para que el overview nunca se quede en blanco
    const combinedGallery = galleryHidden && galleryHidden.length > 0
        ? galleryHidden
        : (gallery ?? []);

    const [currentIndex, setCurrentIndex] = useState(initialIndex);
    const [showOverview, setShowOverview] = useState(openOverview);

    return (
        <motion.section
            className={s.gallery}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
        >
            {/* Close Button */}
            <div className={s.closeGallery} onClick={onClose}>
                <span></span>
                <span></span>
            </div>

            {/* Gallery Overview — cambio instantáneo: un crossfade entre paneles
                deja ver el fondo a mitad de transición (glitch en mobile) */}
                {showOverview && (
                    <div className={s.galleryOverview}>
                        {combinedGallery.map((image, index) => (
                            <div
                                key={`combinedGallery-${index}`}
                                className={s.image}
                                onClick={() => {
                                    setCurrentIndex(index);
                                    setShowOverview(false);
                                }}
                            >
                                <LazyImage
                                    src={image.imageUrl}
                                    width={image.metadata.dimensions.width}
                                    height={image.metadata.dimensions.height}
                                    lqip={image.metadata.lqip || ""}
                                    sizes="(max-width: 768px) 50vw, 20vw"
                                    fill={false}
                                    alt={image.attribution || ""}
                                    caption={image.caption || null}
                                    filename={image.filename || ""}
                                />
                            </div>
                        ))}
                    </div>
                )}

            {/* Swiper Carousel */}
                {!showOverview && (
                    <div className={s.gallerySwipper}>
                        <Swiper
                            modules={[Navigation]}
                            navigation
                            spaceBetween={10}
                            slidesPerView={1}
                            initialSlide={currentIndex}
                            onSlideChange={(swiper) => setCurrentIndex(swiper.activeIndex)}
                        >
                            {combinedGallery.map((image, index) => (
                                <SwiperSlide key={`combinedGallery2-${index}`} className={s.swiperSlide}>
                                    <LazyImage
                                        src={image.imageUrl}
                                        width={image.metadata.dimensions.width}
                                        height={image.metadata.dimensions.height}
                                        lqip={image.metadata.lqip || ""}
                                        sizes="100vw"
                                        fill={false}
                                        alt={image.attribution || ""}
                                        caption={image.caption || null}
                                        filename={image.filename || ""}
                                    />
                                </SwiperSlide>
                            ))}
                            {/* Image Counter */}
                            <motion.div
                                className={s.imageCounter}
                            >
                                {currentIndex + 1} / {combinedGallery.length}
                            </motion.div>
                        </Swiper>


                        {/* Show Overview Button */}
                        <div
                            className={s.overviewButton}
                            onClick={() => setShowOverview(true)}
                        >
                            <p>OVERVIEW</p>
                        </div>
                    </div>
                )}

            {/* Footer */}
            <motion.div
                className={s.galleryFooter}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5 }}
            >
                <div className={s.title}>
                    <h3>{title}</h3>
                </div>
            </motion.div>
        </motion.section>
    );
}